import React, { useState, useEffect } from 'react';
import { Play, Calendar, Eye } from 'lucide-react';
import styles from './youtube.module.css';

const YouTubePage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY || 'AIzaSyDc19QMcJhfV-iEOma0pPnefUsMPTuMWBM';
  const CHANNEL_ID = process.env.REACT_APP_YOUTUBE_CHANNEL_ID || 'UClk1wJ-njNtiBnBviZ3SW-g';
  const MAX_RESULTS = 9;

  useEffect(() => {
    fetchYouTubeVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchYouTubeVideos = async () => {
    try {
      setLoading(true);



      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/search?key=${YOUTUBE_API_KEY}&channelId=${CHANNEL_ID}&part=snippet,id&order=date&maxResults=${MAX_RESULTS}&type=video`
      );

      if (!response.ok) throw new Error('Failed to fetch videos');
      const data = await response.json();

      if (!data.items || data.items.length === 0) {
        setVideos([]);
        setLoading(false);
        return;
      }

      const videoIds = data.items.map(item => item.id.videoId).join(',');
      const statsResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?key=${YOUTUBE_API_KEY}&id=${videoIds}&part=statistics,contentDetails`
      );

      const statsData = await statsResponse.json();

      const videosWithStats = data.items.map((item, index) => {
        const thumbnails = item.snippet.thumbnails;
        let thumbnail = thumbnails.maxres?.url || thumbnails.high?.url || thumbnails.medium?.url || thumbnails.default?.url || `https://img.youtube.com/vi/${item.id.videoId}/hqdefault.jpg`;

        return {
          id: item.id.videoId,
          title: item.snippet.title,
          description: item.snippet.description,
          thumbnail: thumbnail,
          publishedAt: item.snippet.publishedAt,
          channelTitle: item.snippet.channelTitle,
          viewCount: statsData.items[index]?.statistics?.viewCount || '0',
        };
      });

      setVideos(videosWithStats);
      setError(null);

    } catch (err) {
      console.error('Error fetching YouTube videos:', err);
      setError('Failed to load videos. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatViews = (views) => {
    const num = parseInt(views);
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (loading) {
    return (
      <div className={styles.loadingWrapper}>
        <div>
          <div className={styles.spinner}></div>
          <p>Loading videos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorWrapper}>
        <div>
          <p className={styles.errorText}>{error}</p>
          <button onClick={() => fetchYouTubeVideos()} className={styles.tryAgainBtn}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.youtubePage}>
      {/* Hero Section */}
      <div className={styles.heroSection}>
        <div className={styles.container}>
          <h1>YOUTUBE GALLERY</h1>
          <p>
            Explore our architectural projects, design philosophy, and creative process
          </p>
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <div>
            <h2>Latest Projects</h2>
            <div className={styles.headerLine}></div>
          </div>
        </div>

        {/* Video Grid */}
        <div className={styles.grid}>
          {videos.length > 0 ? (
            videos.map((video, index) => (
              <a
                key={video.id}
                href={`https://www.youtube.com/watch?v=${video.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.videoCard}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Thumbnail Fallback */}
                <div className={styles.thumbnailWrapper}>
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className={styles.thumbnail}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const iframe = e.target.nextElementSibling;
                      if (iframe && iframe.tagName === 'IFRAME') iframe.style.display = 'block';
                    }}
                    loading="lazy"
                  />
                  <iframe
                    className={styles.iframeFallback}
                    src={`https://www.youtube.com/embed/${video.id}`}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                  <div className={styles.playOverlay}>
                    <div className={styles.playIcon}>
                      <Play size={24} fill="white" />
                    </div>
                  </div>
                </div>

                {/* Video Info */}
                <div className={styles.videoInfo}>
                  <h3 className={styles.videoTitle}>
                    {video.title}
                  </h3>
                  <div className={styles.videoMeta}>
                    <div className={styles.metaItem}>
                      <Eye size={14} />
                      <span>{formatViews(video.viewCount)}</span>
                    </div>
                    <div className={styles.metaItem}>
                      <Calendar size={14} />
                      <span>{formatDate(video.publishedAt)}</span>
                    </div>
                  </div>
                </div>
              </a>
            ))
          ) : (
            <div className={styles.noVideos}>
              <p>No videos available at the moment.</p>
            </div>
          )}
        </div>

        {/* Subscribe Section */}
        <div className={styles.subscribeSection}>
          <h2>Stay Connected</h2>
          <p>
            Subscribe to our channel for the latest architectural insights, project updates, and design inspiration
          </p>
          <a
            href={`https://www.youtube.com/channel/${CHANNEL_ID}?sub_confirmation=1`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.subscribeBtn}
          >
            <Play size={18} fill="white" />
            Subscribe
          </a>
        </div>
      </div>
    </div>
  );
};

export default YouTubePage;
